import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClipboardCheck, GripVertical, ListOrdered, Pencil, Plus, Trash2, X } from "lucide-react";
import Swal from "sweetalert2";
import "../OffboardingStyles/Offboarding.css";
import {
    createEtapeTemplateRequest,
    deleteEtapeTemplateRequest,
    fetchEtapeTemplatesRequest,
    updateEtapeTemplateRequest,
} from "../../../../redux/actions/offboardingActions";

export default function OffboardingEtapesSettings() {
    const dispatch = useDispatch();
    const { etapeTemplates: templates = [], loading = false } = useSelector(
        (state) => state.offboarding || {}
    );

    const [showModal, setShowModal] = useState(false);
    const [draft, setDraft] = useState({ nom: "", ordreEtape: "" });
    const [editingId, setEditingId] = useState(null);
    const [draggedId, setDraggedId] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        dispatch(fetchEtapeTemplatesRequest());
    }, [dispatch]);

    const sorted = [...templates].sort((a, b) => a.ordreEtape - b.ordreEtape);

    const nextOrder = () =>
        templates.length ? Math.max(...templates.map((t) => t.ordreEtape || 0)) + 1 : 1;

    const openModal = () => {
        setDraft({ nom: "", ordreEtape: nextOrder() });
        setEditingId(null);
        setError("");
        setShowModal(true);
    };

    const openEditModal = (item) => {
        setDraft({ nom: item.nom, ordreEtape: item.ordreEtape });
        setEditingId(item.id);
        setError("");
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setDraft({ nom: "", ordreEtape: "" });
        setEditingId(null);
        setError("");
    };

    const handleSubmit = () => {
        const nom = draft.nom.trim();
        if (!nom) {
            setError("Le nom de l'étape est requis");
            return;
        }
        const ordreEtape = draft.ordreEtape !== "" ? Number(draft.ordreEtape) : nextOrder();
        if (editingId) dispatch(updateEtapeTemplateRequest(editingId, { nom, ordreEtape }));
        else dispatch(createEtapeTemplateRequest({ nom, ordreEtape }));
        closeModal();
    };

    const handleDrop = (target) => {
        if (!draggedId || String(draggedId) === String(target.id)) return;
        const dragged = templates.find((item) => String(item.id) === String(draggedId));
        if (dragged) {
            dispatch(updateEtapeTemplateRequest(dragged.id, {
                nom: dragged.nom,
                ordreEtape: target.ordreEtape,
            }));
        }
        setDraggedId(null);
    };

    const handleDelete = (item) => {
        Swal.fire({
            title: "Confirmation",
            text: `Voulez-vous vraiment supprimer l'étape "${item.nom}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#334785",
            cancelButtonColor: "#204385",
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        }).then((result) => {
            if (result.isConfirmed) dispatch(deleteEtapeTemplateRequest(item.id));
        });
    };

    return (
        <div className="obPage">
            <div className="obWrap">
                <div className="obHeader">
                    <div>
                        <h1>Paramétrage des étapes d'offboarding</h1>
                        <p>Configurez la checklist standard appliquée à chaque départ de collaborateur.</p>
                    </div>
                </div>

                <div className="obCard">
                    <div className="obTopRow">
                        <div>
                            <h2 className="obCard__title">Étapes de la checklist</h2>
                            <span className="obTinyMuted">
                                {templates.length} étape{templates.length > 1 ? "s" : ""} configurée
                                {templates.length > 1 ? "s" : ""}
                            </span>
                        </div>
                        <button className="obBtn obBtn--primary" type="button" onClick={openModal} disabled={loading}>
                            <Plus size={16} /> Ajouter une étape
                        </button>
                    </div>

                    <div className="obTableWrap">
                        <table className="obTable">
                            <thead>
                            <tr>
                                <th style={{ width: 90 }}>Ordre</th>
                                <th>Nom de l'étape</th>
                                <th>Application</th>
                                <th className="is-right">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="obEmpty">Chargement...</td>
                                </tr>
                            ) : sorted.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="obEmpty">
                                        <div className="obSettingsEmpty">
                                            <ClipboardCheck size={28} />
                                            <strong>Aucune étape configurée</strong>
                                            <span>Ajoutez votre première étape pour construire la checklist.</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                sorted.map((item) => (
                                    <tr
                                        key={item.id}
                                        className={String(draggedId) === String(item.id) ? "obDragRow" : ""}
                                        draggable={!loading}
                                        onDragStart={() => setDraggedId(item.id)}
                                        onDragEnd={() => setDraggedId(null)}
                                        onDragOver={(event) => event.preventDefault()}
                                        onDrop={() => handleDrop(item)}
                                    >
                                        <td>
                                            <span className="obDragHandle" title="Glissez pour modifier l'ordre"><GripVertical size={16} /></span>
                                            <span className="obOrderBadge">{item.ordreEtape}</span>
                                        </td>
                                        <td>
                                            <span className="obStrong">{item.nom}</span>
                                        </td>
                                        <td>
                                                <span className="obBadge obBadge--blue">
                                                    Appliquée à tous les collaborateurs
                                                </span>
                                        </td>
                                        <td className="is-right">
                                            <div className="obRowActions">
                                                <button
                                                    className="obIconBtn"
                                                    type="button"
                                                    title="Modifier l'étape"
                                                    onClick={() => openEditModal(item)}
                                                    disabled={loading}
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                                <button
                                                    className="obIconBtn obIconBtn--delete"
                                                    type="button"
                                                    title="Supprimer"
                                                    onClick={() => handleDelete(item)}
                                                    disabled={loading}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {showModal && (
                <div className="obModal">
                    <div className="obModal__panel" role="dialog" aria-modal="true" style={{ width: "min(480px, 100%)" }}>
                        <div className="obModal__header">
                            <div className="obModal__title">{editingId ? "Modifier l'étape" : "Ajouter une étape"}</div>
                            <button
                                className="obModal__close"
                                type="button"
                                onClick={closeModal}
                                aria-label="Fermer"
                                disabled={loading}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="obModal__body">
                            <div className="obModalIntro">
                                <div className="obModalIntro__icon">
                                    <ListOrdered size={20} />
                                </div>
                                <div>
                                    <div className="obModalIntro__title">Nouvelle étape de checklist</div>
                                    <div className="obModalIntro__sub">
                                        Définissez son libellé et sa position dans la séquence.
                                    </div>
                                </div>
                            </div>

                            <div className="obField">
                                <label>
                                    Nom de l'étape <span style={{ color: "#ef4444" }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ex. Restitution du matériel"
                                    value={draft.nom}
                                    onChange={(e) => setDraft((p) => ({ ...p, nom: e.target.value }))}
                                    autoFocus
                                />
                                {error && <span className="obHint" style={{ marginTop: 4 }}>{error}</span>}
                            </div>

                            <div className="obField">
                                <label>Ordre</label>
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="1"
                                    value={draft.ordreEtape}
                                    onChange={(e) => setDraft((p) => ({ ...p, ordreEtape: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div className="obModal__footer">
                            <button className="obBtn obBtn--ghost" type="button" onClick={closeModal} disabled={loading}>
                                Annuler
                            </button>
                            <button
                                className="obBtn obBtn--primary"
                                type="button"
                                onClick={handleSubmit}
                                disabled={loading || !draft.nom.trim()}
                            >
                                {editingId ? <Pencil size={16} /> : <Plus size={16} />} {editingId ? "Enregistrer" : "Créer l'étape"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
